// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { AuditOutput } from 'thehive-sdk';

import { deduplicateAudit, LiveStreamData } from '@Organisms/LiveStreamData/LiveStreamData';

import { STREAM_EVENTS } from '@Services/StreamSrv';

import { useStreamObserver } from '@Hooks/useStream';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';

interface LiveStreamDataProps {
    caseId: string;
    onLoad?: (arg: any[]) => void;
    streamReady?: boolean;
    setCounter?: (num: number) => void;
}

const LiveStream = ({ caseId, onLoad, setCounter, streamReady = false }: LiveStreamDataProps): ReactElement => {
    const [liveFeed, setLiveFeed] = useState<AuditOutput[]>([]);
    const [status, setStatus] = useState('loading');
    const i = useLocalMessage();

    const refreshFeed = useCallback(() => {
        client.StreamController.getInitialFeed(caseId)
            .then((data: AuditOutput[]) => {
                if (data) {
                    setLiveFeed(deduplicateAudit(data));
                    onLoad?.(data);
                }
            })
            .finally(() => {
                setStatus('done');
            });
    }, [caseId]); //eslint-disable-line

    useStreamObserver(
        { rootId: caseId || STREAM_EVENTS.ANY },
        (data) => {
            setLiveFeed(deduplicateAudit([...data, ...liveFeed]));
        },
        [caseId, liveFeed, setLiveFeed],
    );

    useEffect(() => {
        setCounter?.(liveFeed.length);
    }, [setCounter, liveFeed]);

    useEffect((): void => {
        refreshFeed();
    }, [refreshFeed, streamReady]);

    return liveFeed.length && status === 'done' ? (
        <LiveStreamData liveFeed={liveFeed} />
    ) : (
        <DisplayEmptyData spin={status === 'loading'} label={i('stream.none')} />
    );
};

export default LiveStream;
